import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Hash, Tv, ArrowLeft, Play as PlayIcon, RotateCcw, Trophy, Users, Loader2, X } from "lucide-react";
import { useRoom, BACKEND } from "@/useRoom";
import { play } from "@/sounds";
import { SparkleBurst } from "@/components/interactions";

const API = `${BACKEND}/api`;
const WS = "/api/uno/ws";
const CARD_COLORS = ["#e4572e", "#2e86ab", "#17a398", "#f3a712", "#8e44ad"];

const CARD_EMOJI = { wild: "🃏", reverse: "🔄", skip: "⛔", draw2: "+2", draw4: "+4" };
function eventMessage(ev, lang) {
  const es = lang === "es";
  if (ev.card === "wild") return es ? `🃏 ${ev.actor} usó un Comodín` : `🃏 ${ev.actor} used a Wild`;
  const n = ev.amount || 0;
  const num = es ? `${n} ${n === 1 ? "número" : "números"}` : `${n} ${n === 1 ? "number" : "numbers"}`;
  const head = { reverse: "🔄 ¡Reversa!", skip: "⛔ ¡Salta!", draw2: "➕2 ¡Roba 2!", draw4: "➕4 ¡Roba 4!" }[ev.card] || "";
  return es ? `${head} ${ev.victim} perdió ${num} 😱` : `${head} ${ev.victim} lost ${num} 😱`;
}

const L = {
  es: { tag: "Serie Secreta", createTitle: "Crear mesa", createBtn: "Crear mesa", back: "Volver",
    lobbyTitle: "Esperando jugadores", codeLabel: "Código de la mesa", joinHint: "Entra en el teléfono y escribe este código",
    players: "Jugadores", start: "Empezar partida", need: "Se necesita al menos 1 jugador",
    progressTitle: "¡A buscar los números!", found: "encontrados", wins: "¡GANÓ!",
    winner: "Ganador", newgame: "Jugar otra vez", toLobby: "Volver al lobby", almost: "¡a un número de ganar!",
    waiting: "Nadie se ha unido todavía" },
  en: { tag: "Secret Series", createTitle: "Create table", createBtn: "Create table", back: "Back",
    lobbyTitle: "Waiting for players", codeLabel: "Table code", joinHint: "Open your phone and type this code",
    players: "Players", start: "Start game", need: "At least 1 player needed",
    progressTitle: "Find your numbers!", found: "found", wins: "WINS!",
    winner: "Winner", newgame: "Play again", toLobby: "Back to lobby", almost: "one number from winning!",
    waiting: "Nobody has joined yet" },
};

export default function UnoHost() {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const code = params.get("code");
  const [lang, setLang] = useState("es");
  const [creating, setCreating] = useState(false);
  const t = L[lang];

  const createRoom = async () => {
    setCreating(true);
    try {
      const res = await axios.post(`${API}/uno/rooms`, { host_language: lang });
      setParams({ code: res.data.code });
    } finally { setCreating(false); }
  };

  if (!code) {
    return (
      <div className="min-h-screen grain flex flex-col items-center justify-center px-6" style={{ background: "radial-gradient(circle at 30% 20%, #10243a, #0b1220 70%)" }}>
        <button data-testid="unohost-back" onClick={() => nav("/serie")} className="absolute top-6 left-6 inline-flex items-center gap-2 text-sand/70 hover:text-white text-sm"><ArrowLeft className="w-4 h-4" /> {t.back}</button>
        <div className="absolute top-6 right-6 flex gap-2">
          {["es", "en"].map((l) => <button key={l} onClick={() => setLang(l)} className={`px-3 py-1.5 rounded-full text-sm border ${lang === l ? "bg-[#2e86ab] text-white border-[#2e86ab]" : "glass text-parchment border-white/20"}`}>{l === "es" ? "ES" : "EN"}</button>)}
        </div>
        <div className="glass rounded-3xl p-8 border-[#2e86ab]/40 w-full max-w-sm text-center">
          <Hash className="w-14 h-14 text-[#f3a712] mx-auto mb-4" />
          <h1 className="font-serif text-3xl text-white mb-2">{t.createTitle}</h1>
          <p className="text-sand/60 text-sm mb-6">{t.tag}</p>
          <button data-testid="unohost-create-btn" disabled={creating} onClick={createRoom}
            className="btn-tactile w-full bg-[#f3a712] text-midnight border-[#c98f0c] font-bold text-xl rounded-2xl px-6 py-4 disabled:opacity-50">
            {creating ? <Loader2 className="w-6 h-6 animate-spin mx-auto" /> : t.createBtn}
          </button>
        </div>
      </div>
    );
  }
  return <HostRoom code={code} lang={lang} setLang={setLang} t={t} nav={nav} />;
}

function HostRoom({ code, lang, setLang, t, nav }) {
  const { state, send, connected } = useRoom(code, "host", null, WS);
  const prevWinner = useRef(null);
  const [toast, setToast] = useState(null);
  const lastEventId = useRef(0);

  useEffect(() => {
    if (state?.winner && prevWinner.current !== state.winner) {
      prevWinner.current = state.winner;
      play("victory");
    }
  }, [state?.winner]);

  useEffect(() => {
    const ev = state?.last_event;
    if (ev && ev.id && ev.id !== lastEventId.current) {
      lastEventId.current = ev.id;
      play(ev.card === "wild" ? "recovered" : "incorrect");
      const msg = eventMessage(ev, lang);
      setToast({ id: ev.id, msg, card: ev.card });
      const to = setTimeout(() => setToast(null), 3800);
      return () => clearTimeout(to);
    }
  }, [state?.last_event?.id, lang]);

  if (!state) {
    return <div className="min-h-screen flex items-center justify-center bg-midnight text-sand"><Loader2 className="w-8 h-8 animate-spin" /></div>;
  }
  if (state.error) {
    return <div className="min-h-screen flex flex-col items-center justify-center bg-midnight text-sand gap-4"><X className="w-10 h-10 text-incorrect" /><p>Room not found</p><button onClick={() => nav("/serie/host")} className="text-[#f3a712] underline">{t.back}</button></div>;
  }

  const players = state.players || [];
  const bg = { background: "radial-gradient(circle at 50% 0%, #10243a, #0b1220 70%)" };

  return (
    <div className="min-h-screen grain tv-vignette text-white p-8" style={bg} data-testid="unohost-room">
      {toast && (
        <div key={toast.id} className="fixed top-6 left-1/2 -translate-x-1/2 z-50 animate-fade-up" data-testid="unohost-event-toast">
          <div className="glass rounded-2xl px-8 py-4 border-2 shadow-2xl text-center" style={{ borderColor: toast.card === "wild" ? "#f3a712" : "#e4572e" }}>
            <span className="font-display font-800 text-2xl sm:text-3xl text-white">{toast.msg}</span>
          </div>
        </div>
      )}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <Hash className="w-8 h-8 text-[#f3a712]" />
          <span className="font-display text-xl tracking-widest text-[#5fb0d4]">{t.tag}</span>
          <span className={`ml-2 w-2.5 h-2.5 rounded-full ${connected ? "bg-correct" : "bg-incorrect"}`} />
        </div>
        <div className="flex gap-2">
          {["es", "en"].map((l) => <button key={l} onClick={() => setLang(l)} className={`px-3 py-1.5 rounded-full text-sm border ${lang === l ? "bg-[#2e86ab] text-white border-[#2e86ab]" : "glass text-parchment border-white/20"}`}>{l === "es" ? "ES" : "EN"}</button>)}
        </div>
      </div>

      {state.status === "lobby" && (
        <div className="flex flex-col items-center text-center">
          <h1 className="font-display font-800 text-4xl mb-2">{t.lobbyTitle}</h1>
          <p className="text-sand/70 mb-6">{t.joinHint}</p>
          <div className="glass rounded-3xl px-12 py-6 border-[#f3a712]/40 mb-8">
            <div className="text-xs uppercase tracking-widest text-sand/60 mb-1">{t.codeLabel}</div>
            <div data-testid="unohost-code" className="font-display font-800 text-7xl text-[#f3a712] tracking-[0.3em]">{code}</div>
          </div>
          <div className="w-full max-w-3xl">
            <div className="flex items-center gap-2 text-sand/70 mb-3"><Users className="w-5 h-5" /> {t.players} · {players.length}</div>
            {players.length === 0 ? <p className="text-sand/40 italic">{t.waiting}</p> : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {players.map((p) => (
                  <div key={p.id} data-testid={`unohost-lobby-${p.id}`} className="glass rounded-2xl px-4 py-3 border flex items-center gap-3 animate-fade-up" style={{ borderColor: `${p.color}66` }}>
                    <span className="w-9 h-9 rounded-full shrink-0" style={{ background: p.color }} />
                    <span className="font-serif text-lg truncate">{p.name}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <button data-testid="unohost-start-btn" disabled={players.length < 1} onClick={() => { play("page"); send({ action: "start" }); }}
            className="btn-tactile mt-10 bg-[#f3a712] text-midnight border-[#c98f0c] font-black text-2xl rounded-full px-12 py-5 inline-flex items-center gap-3 disabled:opacity-40">
            <PlayIcon className="w-7 h-7" /> {t.start}
          </button>
          {players.length < 1 && <p className="text-sand/40 text-sm mt-3">{t.need}</p>}
        </div>
      )}

      {(state.status === "playing" || state.status === "finished") && (
        <div>
          <div className="text-center mb-8">
            {state.status === "finished" && state.winner_name ? (
              <div className="relative inline-block">
                <SparkleBurst count={40} />
                <h1 className="font-display font-800 text-5xl text-[#f3a712] flex items-center justify-center gap-3"><Trophy className="w-12 h-12" /> {state.winner_name} {t.wins}</h1>
              </div>
            ) : (
              <h1 className="font-display font-800 text-4xl">{t.progressTitle}</h1>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-6xl mx-auto">
            {[...players].sort((a, b) => (b.found_count - a.found_count)).map((p) => {
              const near = p.found_count === p.total - 1 && !p.done;
              const isWinner = state.winner === p.id;
              return (
                <div key={p.id} data-testid={`unohost-player-${p.id}`}
                  className={`glass rounded-3xl p-5 border-2 transition-all ${isWinner ? "ring-2 ring-[#f3a712] scale-[1.02]" : ""} ${near ? "animate-pulse-gold" : ""}`}
                  style={{ borderColor: p.done ? "#f3a712" : `${p.color}66` }}>
                  <div className="flex items-center gap-3 mb-4">
                    <span className="w-11 h-11 rounded-full shrink-0 flex items-center justify-center font-bold text-white" style={{ background: p.color }}>{p.win_rank || ""}</span>
                    <div className="min-w-0 flex-1">
                      <div className="font-serif text-xl truncate">{p.name}</div>
                      <div className="text-sand/60 text-xs">{p.found_count}/{p.total} {t.found}{near ? ` · ${t.almost}` : ""}</div>
                    </div>
                    {p.done && <Trophy className="w-6 h-6 text-[#f3a712]" />}
                  </div>
                  <div className="flex gap-2 justify-center">
                    {Array.from({ length: p.total }).map((_, i) => (
                      <span key={i} className="flex-1 h-10 rounded-lg border-2 transition-all"
                        style={i < p.found_count
                          ? { background: CARD_COLORS[i % CARD_COLORS.length], borderColor: CARD_COLORS[i % CARD_COLORS.length] }
                          : { background: "rgba(255,255,255,0.04)", borderColor: "rgba(255,255,255,0.15)" }} />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex justify-center gap-4 mt-10">
            <button data-testid="unohost-newgame-btn" onClick={() => { play("page"); send({ action: "newgame" }); }}
              className="btn-tactile glass border-2 border-[#f3a712]/50 text-white rounded-full px-8 py-4 inline-flex items-center gap-2 font-bold"><RotateCcw className="w-5 h-5" /> {t.newgame}</button>
            <button data-testid="unohost-lobby-btn" onClick={() => send({ action: "reset" })}
              className="btn-tactile glass border border-white/20 text-sand rounded-full px-8 py-4 inline-flex items-center gap-2"><Users className="w-5 h-5" /> {t.toLobby}</button>
          </div>
        </div>
      )}
    </div>
  );
}
